import { useState, useMemo, useEffect } from "react";
import AdminLayout from "@/pages/admin/components/AdminLayout";
import ProductModal from "./components/ProductModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import { supabase, Product, Category } from "@/lib/supabase";

const AdminProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterFeatured, setFilterFeatured] = useState<"all" | "featured" | "not-featured">("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = async () => {
    setLoading(true);
    const [{ data: prods }, { data: cats }] = await Promise.all([
      supabase.from("products").select("*").order("created_at", { ascending: false }),
      supabase.from("categories").select("*").order("name"),
    ]);
    setProducts(prods || []);
    setCategories(cats || []);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [categories]);

  const filtered = useMemo(() => {
    let list = products;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }
    if (filterCat !== "all") list = list.filter((p) => p.category === filterCat);
    if (filterFeatured === "featured") list = list.filter((p) => p.is_featured);
    if (filterFeatured === "not-featured") list = list.filter((p) => !p.is_featured);
    return list;
  }, [products, search, filterCat, filterFeatured]);

  const handleSubmit = async (form: Omit<Product, "created_at">) => {
    setSaving(true);
    if (editProduct) {
      const { error } = await supabase.from("products").update({
        title: form.title, description: form.description,
        long_description: form.long_description || null,
        highlights: form.highlights || null,
        price: form.price,
        discounted_price: form.discounted_price || null,
        image: form.images?.[0] || form.image, images: form.images || [],
        category: form.category, badge: form.badge || null,
        is_featured: form.is_featured, purchase_options: form.purchase_options || [],
      }).eq("id", editProduct.id);
      if (error) showToast("Failed to update product.", "error");
      else { showToast("Product updated!"); await fetchData(); }
    } else {
      const newId = Date.now().toString();
      const { error } = await supabase.from("products").insert({
        id: newId, title: form.title, description: form.description,
        long_description: form.long_description || null,
        highlights: form.highlights || null,
        price: form.price,
        discounted_price: form.discounted_price || null,
        image: form.images?.[0] || form.image, images: form.images || [],
        category: form.category, badge: form.badge || null,
        is_featured: form.is_featured, purchase_options: form.purchase_options || [],
      });
      if (error) showToast("Failed to add product.", "error");
      else { showToast("Product added!"); await fetchData(); }
    }
    setSaving(false);
    setModalOpen(false);
    setEditProduct(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("products").delete().eq("id", deleteTarget.id);
    if (error) showToast("Failed to delete.", "error");
    else { showToast("Product deleted.", "error"); await fetchData(); }
    setDeleteTarget(null);
  };

  const toggleFeatured = async (product: Product) => {
    const { error } = await supabase.from("products").update({ is_featured: !product.is_featured }).eq("id", product.id);
    if (!error) setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, is_featured: !p.is_featured } : p));
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {toast && (
          <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl text-white text-sm font-medium ${toast.type === "success" ? "bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]" : "bg-red-500"}`}>
            <i className={toast.type === "success" ? "ri-check-line text-lg" : "ri-delete-bin-line text-lg"} />
            {toast.msg}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Products</h1>
            <p className="text-sm text-gray-500 mt-0.5">{products.length} total products</p>
          </div>
          <button onClick={() => { setEditProduct(null); setModalOpen(true); }} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap">
            <i className="ri-add-line text-base" /> Add Product
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] text-sm text-gray-700 placeholder-gray-400 outline-none transition-all" />
          </div>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="px-4 py-2.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] text-sm text-gray-700 outline-none cursor-pointer">
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterFeatured} onChange={(e) => setFilterFeatured(e.target.value as "all" | "featured" | "not-featured")} className="px-4 py-2.5 rounded-xl bg-gray-50 border-2 border-transparent focus:border-[#3a8cbf] text-sm text-gray-700 outline-none cursor-pointer">
            <option value="all">All Products</option>
            <option value="featured">Featured Only</option>
            <option value="not-featured">Not Featured</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                  <i className="ri-gift-line text-2xl text-gray-300" />
                </div>
                <p className="text-gray-500 font-medium">No products found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px]">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Badge</th>
                      <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Featured</th>
                      <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((product, idx) => (
                      <tr key={product.id} className={`border-b border-gray-50 hover:bg-gray-50/60 transition-colors ${idx === filtered.length - 1 ? "border-0" : ""}`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <img src={product.image} alt={product.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-gray-100" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">{product.title}</p>
                              <p className="text-xs text-gray-400 truncate max-w-[200px] mt-0.5">{product.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4"><span className="text-sm text-gray-600">{categoryMap[product.category] || product.category}</span></td>
                        <td className="px-5 py-4">
                          {product.discounted_price ? (
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-[#e05a8a]">₹{product.discounted_price}</span>
                              <span className="text-xs text-gray-400 line-through">₹{product.price}</span>
                            </div>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900">₹{product.price}</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          {product.badge ? (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-[#3a8cbf]/10 to-[#e05a8a]/10 text-[#3a8cbf]">{product.badge}</span>
                          ) : <span className="text-xs text-gray-300">—</span>}
                        </td>
                        <td className="px-5 py-4 text-center">
                          <button onClick={() => toggleFeatured(product)} className={`relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer mx-auto flex-shrink-0 ${product.is_featured ? "bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]" : "bg-gray-200"}`}>
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${product.is_featured ? "translate-x-5" : "translate-x-0"}`} />
                          </button>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => { setEditProduct(product); setModalOpen(true); }} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-[#3a8cbf]/10 text-gray-500 hover:text-[#3a8cbf] cursor-pointer transition-colors">
                              <i className="ri-edit-line text-sm" />
                            </button>
                            <button onClick={() => setDeleteTarget(product)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-red-50 text-gray-500 hover:text-red-500 cursor-pointer transition-colors">
                              <i className="ri-delete-bin-6-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {modalOpen && (
        <ProductModal
          product={editProduct}
          categories={categories}
          onSave={() => {}}
          onClose={() => { setModalOpen(false); setEditProduct(null); }}
          saving={saving}
          onSubmit={handleSubmit}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          title={deleteTarget.title}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminProductsPage;
